/**
 * Extracts error message from API error responses
 * Standardized format: { message, code?, details? }
 * @param {Error|Object} error - Error object
 * @returns {string} Error message
 */
export const extractErrorMessage = (error) => {
    if (!error) {
        return 'Unknown error';
    }

    // Redux thunks often reject with a plain string; axios message alone is also common
    if (typeof error === 'string') {
        const t = error.trim();
        return t || 'Unknown error';
    }

    // Check for nested error objects (common in axios)
    if (error.response) {
        const data = error.response.data;
        if (data) {
            // Backend returns { message: "..." }
            if (data.message) {
                return data.message;
            }
            // Sometimes errors are in an 'error' field
            if (data.error) {
                return typeof data.error === 'string' ? data.error : data.error.message || JSON.stringify(data.error);
            }
            // If data itself is a string
            if (typeof data === 'string') {
                return data;
            }
        }
    }

    // Check for direct error property
    if (error.error) {
        return typeof error.error === 'string' ? error.error : error.error.message || JSON.stringify(error.error);
    }

    // Check for message property
    if (error.message) {
        const m = String(error.message).trim();
        if (m) {
            return m;
        }
    }

    return 'Unknown error';
};

/**
 * Extracts error code from API error responses
 * @param {Error|Object} error - Error object
 * @returns {string|null} Error code or null
 */
export const extractErrorCode = (error) => {
    if (!error) {
        return null;
    }

    return error.response?.data?.code || null;
};

/**
 * Creates an error handler for async thunks
 * @param {Object} logger - Logger instance
 * @param {string} context - Context for logging
 * @returns {Function} Error handler function
 */
export const createErrorHandler = (logger, context) => {
    return (error) => {
        const message = extractErrorMessage(error);
        const code = extractErrorCode(error);
        logger.error(`${context} failed`, {
            error: message,
            code,
            originalError: error
        });
        return { error: message, code };
    };
};

